import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  X,
  SlidersHorizontal,
  ChevronRight,
  ChevronDown,
  Check,
  Calendar,
  ArrowLeft,
  RotateCcw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  PAST_DATE_PRESETS,
  FUTURE_DATE_PRESETS,
  dateFilterLabel,
  isFilterActiveValue,
  type FilterOption,
} from "@/lib/filters";

export interface FilterField {
  name: string;
  label: string;
  icon?: React.ElementType;
  /** "select" (options) or "date" (presets + custom range) */
  type: "select" | "date";
  options?: FilterOption[];
  /** For date fields: past (created on) or future (close / due) presets */
  dates?: "past" | "future";
  defaultValue: string;
  /** Hide the field (e.g. owner filter for someone who only sees their own records) */
  hidden?: boolean;
}

interface FilterBarProps {
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    onEnter?: () => void;
  };
  fields: FilterField[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onReset: () => void;
  /** Rows after filtering / before filtering, and what they are ("leads") */
  shown?: number;
  total?: number;
  noun?: string;
  /** Page-specific controls shown at the end of the first row (status buttons, view toggle…) */
  children?: React.ReactNode;
  className?: string;
}

/**
 * FilterBar:
 * - Search input with clean icon alignment (no text overlap)
 * - Single "Filters" dropdown button opening a compact cascading flyout menu:
 *   Main filter category list -> Hover/navigate shows side child options menu
 * - Fixed and robust custom date filter (From / To inputs + Apply Range)
 * - Active filter badges and removable chips
 */
export function FilterBar({
  search,
  fields,
  values,
  onChange,
  onReset,
  shown,
  total,
  noun = "records",
  children,
  className,
}: FilterBarProps) {
  const visible = fields.filter((f) => !f.hidden);
  const activeFields = visible.filter((f) => isFilterActiveValue(f.defaultValue, values[f.name], f.type));
  const searching = Boolean(search?.value.trim());

  // Cascading Dropdown State (no default category selected on open)
  const [open, setOpen] = useState(false);
  const [activeFieldName, setActiveFieldName] = useState<string | null>(null);
  const [childSearch, setChildSearch] = useState("");
  const [mobileChildOpen, setMobileChildOpen] = useState(false);
  const [flyoutSide, setFlyoutSide] = useState<"right" | "left">("right");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const mainMenuRef = useRef<HTMLDivElement>(null);

  // Clear active field if it is no longer visible
  useEffect(() => {
    if (activeFieldName && !visible.some((f) => f.name === activeFieldName)) {
      setActiveFieldName(null);
    }
  }, [visible, activeFieldName]);

  // Determine flyout placement (prevent overflowing window right edge)
  useEffect(() => {
    if (open && mainMenuRef.current) {
      const rect = mainMenuRef.current.getBoundingClientRect();
      if (rect.right + 270 > window.innerWidth) {
        setFlyoutSide("left");
      } else {
        setFlyoutSide("right");
      }
    }
  }, [open, activeFieldName]);

  // Handle outside click & keyboard navigation
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
        setActiveFieldName(null);
        setMobileChildOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        setActiveFieldName(null);
        setMobileChildOpen(false);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        if (visible.length === 0) return;
        const currentIndex = visible.findIndex((f) => f.name === activeFieldName);
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % visible.length;
        const target = visible[nextIndex];
        if (target) {
          setActiveFieldName(target.name);
          setChildSearch("");
        }
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        if (visible.length === 0) return;
        const currentIndex = visible.findIndex((f) => f.name === activeFieldName);
        const prevIndex = currentIndex <= 0 ? visible.length - 1 : currentIndex - 1;
        const target = visible[prevIndex];
        if (target) {
          setActiveFieldName(target.name);
          setChildSearch("");
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, activeFieldName, visible]);

  const valueLabel = (f: FilterField, v: string) =>
    f.type === "date"
      ? dateFilterLabel(v, f.dates === "future" ? FUTURE_DATE_PRESETS : PAST_DATE_PRESETS)
      : f.options?.find((o) => o.value === v)?.label || v;

  const currentField = activeFieldName ? visible.find((f) => f.name === activeFieldName) || null : null;
  const currentValue = currentField ? values[currentField.name] ?? currentField.defaultValue : "";

  return (
    <div className={cn("bg-card border border-border rounded-xl p-3 space-y-2.5", className)}>
      <div className="flex flex-wrap items-center gap-2.5">
        {/* 1. Search Box with properly spaced icon (never overlapping) */}
        {search && (
          <div className="relative w-full sm:w-72 md:w-80 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none select-none z-10" />
            <Input
              placeholder={search.placeholder}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search.onEnter?.()}
              className="pl-10 pr-8 h-9 text-xs sm:text-sm bg-background border-border/80 rounded-lg shadow-2xs"
            />
            {searching && (
              <button
                type="button"
                onClick={() => search.onChange("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded-sm transition-colors cursor-pointer"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        )}

        {/* 2. Main Filter Cascading Dropdown */}
        {visible.length > 0 && (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => {
                const nextOpen = !open;
                setOpen(nextOpen);
                setActiveFieldName(null);
                setMobileChildOpen(false);
                setChildSearch("");
              }}
              className={cn(
                "h-9 px-3 rounded-lg border text-xs sm:text-sm font-medium inline-flex items-center gap-2 transition-all shadow-2xs select-none cursor-pointer",
                open || activeFields.length > 0
                  ? "bg-primary/10 border-primary/40 text-primary hover:bg-primary/15"
                  : "bg-background border-border text-foreground hover:bg-muted/60"
              )}
            >
              <SlidersHorizontal className="size-3.5 text-current shrink-0" />
              <span>Filters</span>
              {activeFields.length > 0 && (
                <span className="size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0">
                  {activeFields.length}
                </span>
              )}
              <ChevronDown
                className={cn(
                  "size-3.5 opacity-60 transition-transform duration-200 shrink-0",
                  open && "rotate-180"
                )}
              />
            </button>

            {/* Cascading Menu Container */}
            {open && (
              <div
                className="absolute left-0 top-full mt-1.5 z-50 flex items-start"
                onMouseLeave={() => {
                  if (!mobileChildOpen) setActiveFieldName(null);
                }}
              >
                {/* Main Filter List (Compact popover) */}
                <div
                  ref={mainMenuRef}
                  className={cn(
                    "w-48 sm:w-52 bg-popover text-popover-foreground border border-border rounded-xl shadow-xl py-1 z-20 overflow-hidden animate-in fade-in-50 zoom-in-95 duration-100",
                    mobileChildOpen && "hidden sm:block"
                  )}
                >
                  <div className="px-2.5 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Filters
                  </div>

                  <div className="space-y-0.5 px-1">
                    {visible.map((f) => {
                      const Icon = f.icon;
                      const isFieldActive = isFilterActiveValue(f.defaultValue, values[f.name], f.type);
                      const isSelected = f.name === currentField?.name;

                      return (
                        <button
                          key={f.name}
                          type="button"
                          onClick={() => {
                            setActiveFieldName(f.name);
                            setMobileChildOpen(true);
                            setChildSearch("");
                          }}
                          onMouseEnter={() => {
                            setActiveFieldName(f.name);
                            setChildSearch("");
                          }}
                          className={cn(
                            "w-full text-left px-2.5 py-2 text-xs rounded-lg flex items-center justify-between gap-2 transition-colors cursor-pointer group",
                            isSelected
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-foreground hover:bg-muted/70 hover:text-foreground"
                          )}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {Icon && (
                              <Icon
                                className={cn(
                                  "size-3.5 shrink-0 transition-colors",
                                  isSelected
                                    ? "text-primary"
                                    : isFieldActive
                                    ? "text-primary"
                                    : "text-muted-foreground"
                                )}
                              />
                            )}
                            <span className="truncate flex-1">{f.label}</span>
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0">
                            {isFieldActive && (
                              <span className="size-1.5 rounded-full bg-primary" title="Filter active" />
                            )}
                            <ChevronRight
                              className={cn(
                                "size-3.5 transition-transform",
                                isSelected ? "text-primary" : "text-muted-foreground/60"
                              )}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {activeFields.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-border/60 px-1">
                      <button
                        type="button"
                        onClick={() => {
                          onReset();
                          setOpen(false);
                          setMobileChildOpen(false);
                        }}
                        className="w-full text-left px-2.5 py-1.5 text-xs text-destructive hover:bg-destructive/10 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <RotateCcw className="size-3" />
                        <span>Reset all filters</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Side Child Flyout Submenu */}
                {currentField && (
                  <div
                    className={cn(
                      "w-56 sm:w-64 bg-popover text-popover-foreground border border-border rounded-xl shadow-xl py-1 z-30 animate-in fade-in-50 zoom-in-95 duration-100 flex flex-col max-h-[380px] overflow-hidden",
                      flyoutSide === "right"
                        ? "sm:absolute sm:left-full sm:top-0 sm:ml-1.5"
                        : "sm:absolute sm:right-full sm:top-0 sm:mr-1.5",
                      !mobileChildOpen && "hidden sm:flex"
                    )}
                  >
                    <ChildOptionsPanel
                      field={currentField}
                      currentValue={currentValue}
                      childSearch={childSearch}
                      setChildSearch={setChildSearch}
                      onChange={(val) => {
                        onChange(currentField.name, val);
                        setOpen(false);
                        setMobileChildOpen(false);
                      }}
                      onBack={() => setMobileChildOpen(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Page-specific children (e.g. view toggle or action buttons) */}
        {children && <div className="flex items-center gap-2 sm:ml-auto">{children}</div>}
      </div>

      {/* 3. Active filter chips row with 'Clear all' and result count */}
      {(activeFields.length > 0 || searching || shown !== undefined) && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs pt-1 border-t border-border/40">
          {activeFields.map((f) => (
            <span
              key={f.name}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 animate-in fade-in-50 duration-150"
            >
              <span className="text-primary/70">{f.label}:</span>
              <span className="font-medium max-w-[130px] sm:max-w-[180px] truncate">
                {valueLabel(f, values[f.name]!)}
              </span>
              <button
                type="button"
                onClick={() => onChange(f.name, f.defaultValue)}
                aria-label={`Remove ${f.label} filter`}
                className="size-4 rounded-full hover:bg-primary/20 flex items-center justify-center transition-colors cursor-pointer text-primary/80 hover:text-primary"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}

          {(activeFields.length > 0 || searching) && (
            <button
              type="button"
              onClick={() => {
                onReset();
                search?.onChange("");
              }}
              className="px-2 py-0.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted font-medium transition-colors cursor-pointer"
            >
              Clear all
            </button>
          )}

          {shown !== undefined && (
            <span className="ml-auto text-muted-foreground text-xs">
              Showing <strong className="text-foreground">{shown}</strong>
              {total !== undefined && total !== shown && (
                <>
                  {" "}
                  of <strong className="text-foreground">{total}</strong>
                </>
              )}{" "}
              {noun}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Side Child Options Panel for the active category */
function ChildOptionsPanel({
  field: f,
  currentValue,
  childSearch,
  setChildSearch,
  onChange,
  onBack,
}: {
  field: FilterField;
  currentValue: string;
  childSearch: string;
  setChildSearch: (s: string) => void;
  onChange: (v: string) => void;
  onBack: () => void;
}) {
  const isDate = f.type === "date";
  const presets = isDate
    ? f.dates === "future"
      ? FUTURE_DATE_PRESETS.filter((p) => p.value !== "custom")
      : PAST_DATE_PRESETS.filter((p) => p.value !== "custom")
    : f.options || [];

  const isFieldActive = isFilterActiveValue(f.defaultValue, currentValue, f.type);
  const showSearch = !isDate && presets.length > 6;
  const filteredPresets = showSearch && childSearch.trim()
    ? presets.filter((p) => p.label.toLowerCase().includes(childSearch.toLowerCase()))
    : presets;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-2.5 py-1.5 border-b border-border/50 flex items-center justify-between gap-2 shrink-0 bg-muted/20">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="sm:hidden text-muted-foreground hover:text-foreground p-0.5 rounded cursor-pointer"
            aria-label="Back to categories"
          >
            <ArrowLeft className="size-3.5" />
          </button>
          <span className="text-xs font-semibold text-foreground truncate">
            {f.label}
          </span>
        </div>

        {isFieldActive && (
          <button
            type="button"
            onClick={() => onChange(f.defaultValue)}
            className="text-[11px] text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            Reset
          </button>
        )}
      </div>

      {/* Search within options */}
      {showSearch && (
        <div className="p-1.5 border-b border-border/40 shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={`Search ${f.label.toLowerCase()}...`}
              value={childSearch}
              onChange={(e) => setChildSearch(e.target.value)}
              className="w-full pl-8 pr-2 py-1 text-xs rounded bg-muted/40 border border-border/60 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      )}

      {/* Preset Options List */}
      <div className="overflow-y-auto p-1 space-y-0.5 flex-1 text-xs">
        {filteredPresets.length === 0 && (
          <div className="py-4 text-center text-xs text-muted-foreground">
            No options found
          </div>
        )}

        {filteredPresets.map((opt) => {
          const isSelected = currentValue === opt.value;

          return (
            <div
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={cn(
                "flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-colors gap-2",
                isSelected
                  ? "bg-primary/10 text-primary font-medium"
                  : "hover:bg-muted/70 text-foreground"
              )}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div
                  className={cn(
                    "size-3.5 rounded-full border flex items-center justify-center shrink-0 transition-colors",
                    isSelected
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-border/80"
                  )}
                >
                  {isSelected && <Check className="size-2 stroke-[3]" />}
                </div>
                <span className="truncate flex-1">{opt.label}</span>
              </div>

              {opt.count !== undefined && (
                <span className="text-[10px] text-muted-foreground tabular-nums px-1.5 py-0.2 rounded bg-muted/60 shrink-0">
                  {opt.count}
                </span>
              )}
            </div>
          );
        })}

        {/* Custom Date Range Component */}
        {isDate && (
          <CustomDateSection
            field={f}
            currentValue={currentValue}
            onApply={(from, to) => onChange(`custom:${from}:${to}`)}
            onClear={() => onChange(f.defaultValue)}
          />
        )}
      </div>
    </div>
  );
}

/** Custom Date Range input with validation and explicit Apply button */
function CustomDateSection({
  field,
  currentValue,
  onApply,
  onClear,
}: {
  field: FilterField;
  currentValue: string;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}) {
  const isCustomActive = currentValue.startsWith("custom");
  const [initFrom = "", initTo = ""] = isCustomActive ? currentValue.split(":").slice(1) : [];

  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);

  useEffect(() => {
    if (currentValue.startsWith("custom")) {
      const parts = currentValue.split(":");
      setFrom(parts[1] || "");
      setTo(parts[2] || "");
    } else {
      setFrom("");
      setTo("");
    }
  }, [currentValue]);

  const hasAnyDate = Boolean(from.trim() || to.trim());

  const handleApply = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!hasAnyDate) return;
    onApply(from.trim(), to.trim());
  };

  return (
    <div className="mt-1 pt-1.5 border-t border-border/50">
      <div className="p-2 rounded-lg bg-muted/40 border border-border/60 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
            <Calendar className="size-3 text-primary" />
            Custom Range
          </span>
          {isCustomActive && (
            <button
              type="button"
              onClick={onClear}
              className="text-[10px] text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>

        <form onSubmit={handleApply} className="space-y-2">
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground font-medium">From</label>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full h-7 px-1.5 text-[11px] rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground font-medium">To</label>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                className="w-full h-7 px-1.5 text-[11px] rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={!hasAnyDate}
            className={cn(
              "w-full h-7 text-xs font-medium rounded transition-colors cursor-pointer shadow-2xs flex items-center justify-center",
              hasAnyDate
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed opacity-60"
            )}
          >
            Apply Range
          </button>
        </form>
      </div>
    </div>
  );
}

export default FilterBar;
